import * as formik from 'formik';
import * as yup from 'yup';
import { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { createBusinessPartner, editBusinessPartner, getBusinessPartnerForm } from '../../utils/api';
import Loader from '../Loader';
import Success from '../Success'
import Error from '../Error'
import { useDataStore } from '../../Store';
import { faChainSlash } from '@fortawesome/free-solid-svg-icons';

const CreateEditBusinessPartnerModal = ({ show, onHide, businessPartner }) => {

    const selectedEntity = useDataStore((state) => state.selectedEntity);

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const isCreate = !businessPartner;
    businessPartner = businessPartner || {};

    var id = businessPartner?.Id;

    const fetchData = async () => {
        setLoading(true);
        const data = await getBusinessPartnerForm();
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    const resetState = () => {
        setData({});
        setError(null);
        setLoading(false);
    };

    useEffect(() => {
        const onLoad = () => {
            if (show)
                fetchData();
            else
                resetState();
        }
        onLoad();
    }, [show]);

    const closeModal = (shouldRefresh) => {
        setLoading(true);
        setIsSaving(false);
        setError(false);
        setIsSaved(false);
        onHide(shouldRefresh);
    }

    const { Formik } = formik;

    const schema = yup.object().shape({
        name: yup.string().required('This field is required'),
        businessPartnerTypeId: yup.string().required('This field is required'),
        taxIdNumber: yup.string(),
        addressLine1: yup.string().required('This field is required'),
        city: yup.string().required('This field is required'),
        state: yup.string().required('This field is required'),
        postalCode: yup.string().required('This field is required'),
    });



    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop={true} style={{ zIndex: 1051 }} centered>
            <Modal.Header closeButton>
                <Modal.Title>{isCreate ? 'Create Business Partner' : 'Edit Business Partner (Id = ' + id + ")"}</Modal.Title>
            </Modal.Header>

            {loading || isSaving ? (
                <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader showText={true} text={isSaving && 'Saving...'} />
                </div>
            ) : (
                error ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Error />
                    </div>
                ) : isSaved ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Success />
                    </div>
                ) : (
                    <Modal.Body>
                        <Formik
                            validationSchema={schema}
                            onSubmit={async (form) => {
                                form.id = id;

                                setLoading(true);
                                const res = isCreate ? await createBusinessPartner(selectedEntity, form) : await editBusinessPartner(selectedEntity, form);
                                if (res) {
                                    setIsSaved(true);
                                }
                                else {
                                    setError(true);
                                }
                                setIsSaving(false);
                                setLoading(false);

                                if (res)
                                    setTimeout(() => closeModal(true), 2000);
                            }}
                            initialValues={{
                                name: businessPartner?.Name || '',
                                businessPartnerTypeId: businessPartner?.BusinessPartnerType?.Id || '',
                                taxIdNumber: businessPartner?.TaxIdNumber || '',
                                businessPartnerNumber: businessPartner?.BusinessPartnerNumber || '',
                                addressLine1: businessPartner?.Address?.AddressLine1 || '',
                                addressLine2: businessPartner?.Address?.AddressLine2 || '',
                                city: businessPartner?.Address?.City || '',
                                state: businessPartner?.Address?.State || '',
                                postalCode: businessPartner?.Address?.PostalCode || ''
                            }}
                        >
                            {({ handleSubmit, handleChange, values, touched, errors }) => (

                                <Form noValidate onSubmit={handleSubmit}>
                                    <Row>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="name" value={values.name} onChange={handleChange} isInvalid={errors.name} isValid={values.name && !errors.name} aria-label="Name" />
                                                <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="name"><span style={{ color: 'red' }}>*</span>Name</Form.Label>
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control
                                                    id='businessPartnerTypeId'
                                                    name='businessPartnerTypeId'
                                                    as="select"
                                                    value={values.businessPartnerTypeId}
                                                    isInvalid={errors.businessPartnerTypeId} isValid={values.businessPartnerTypeId && !errors.businessPartnerTypeId} aria-label="Type"
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select a Type</option>

                                                    {data?.BusinessPartnerTypes?.map((o, index) => (
                                                        <option key={o.id} value={o.Id}>{o.Description}</option>
                                                    ))}
                                                </Form.Control>
                                                <Form.Control.Feedback type='invalid'>{errors.businessPartnerTypeId}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor='businessPartnerTypeId'><span style={{ color: 'red' }}>*</span>Type</Form.Label>
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="taxIdNumber" value={values.taxIdNumber} onChange={handleChange} isInvalid={errors.taxIdNumber} isValid={values.taxIdNumber && !errors.taxIdNumber} aria-label="Tax Id Number (EIN, SSN, etc.)" />
                                                <Form.Control.Feedback type='invalid'>{errors.taxIdNumber}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="taxIdNumber">Tax Id Number (EIN, SSN, etc.)</Form.Label>
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="businessPartnerNumber" value={values.businessPartnerNumber} onChange={handleChange} isInvalid={errors.businessPartnerNumber} isValid={values.businessPartnerNumber && !errors.businessPartnerNumber} aria-label="Business Partner Number" />
                                                <Form.Control.Feedback type='invalid'>{errors.businessPartnerNumber}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="businessPartnerNumber">Business Partner Number</Form.Label>
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="addressLine1" value={values.addressLine1} onChange={handleChange} isInvalid={errors.addressLine1} isValid={values.addressLine1 && !errors.addressLine1} aria-label="Address" />
                                                <Form.Control.Feedback type='invalid'>{errors.addressLine1}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="addressLine1"><span style={{ color: 'red' }}>*</span>Address</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control id="addressLine2" value={values.addressLine2} onChange={handleChange} isInvalid={errors.addressLine2} isValid={values.addressLine2 && !errors.addressLine2} aria-label="Address 2" />
                                                <Form.Control.Feedback type='invalid'>{errors.addressLine2}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="addressLine2">Address 2</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control id="city" value={values.city} onChange={handleChange} isInvalid={errors.city} isValid={values.city && !errors.city} aria-label="City" />
                                                <Form.Control.Feedback type='invalid'>{errors.city}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="city"><span style={{ color: 'red' }}>*</span>City</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control
                                                    id='state'
                                                    as="select"
                                                    value={values.state}
                                                    isInvalid={errors.state} isValid={values.state && !errors.state} aria-label="State"
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select a state</option>
                                                    {data?.States?.map((o, index) => (
                                                        <option key={o.Code} value={o.Code}>{o.Name}</option>
                                                    ))}
                                                </Form.Control>
                                                <Form.Control.Feedback type='invalid'>{errors.state}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="state"><span style={{ color: 'red' }}>*</span>State</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control id="postalCode" value={values.postalCode} onChange={handleChange} isInvalid={errors.postalCode} isValid={values.postalCode && !errors.postalCode} aria-label="Zip" />
                                                <Form.Control.Feedback type='invalid'>{errors.postalCode}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="postalCode"><span style={{ color: 'red' }}>*</span>Zip</Form.Label>
                                        </Col>
                                    </Row>
                                    <br />
                                    <Button style={{ width: '100%' }} type="submit">{isCreate ? "Create Business Partner" : "Save"}</Button>
                                </Form>
                            )}
                        </Formik>
                    </Modal.Body>
                ))}
        </Modal>
    );
};

export default CreateEditBusinessPartnerModal;